import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { ScoreHistoryChartProps } from '@/types/components'
import { ScoreBand } from '@/types'
import { format, parseISO } from 'date-fns'

const BAND_THRESHOLDS = {
  'A+': 90,
  'A': 80,
  'B+': 70,
  'B': 60,
  'C+': 50,
  'C': 40,
  'D': 0
}

const BAND_COLORS = {
  'A+': '#10b981',
  'A': '#10b981',
  'B+': '#3b82f6',
  'B': '#3b82f6',
  'C+': '#f59e0b',
  'C': '#f97316',
  'D': '#ef4444'
}

export function ScoreHistoryAreaChart({
  data,
  timeRange = '30d',
  showBands = true,
  height = 400,
  className
}: Omit<ScoreHistoryChartProps, 'showTrendLine'>) {
  // Format data for chart
  const chartData = data.map(item => ({
    ...item,
    date: format(parseISO(item.date), 'MMM dd'),
    fullDate: item.date,
    bandColor: BAND_COLORS[item.band]
  }))

  // Calculate gradient colors based on score ranges
  const getGradientColor = (score: number): string => {
    if (score >= 90) return '#10b981'
    if (score >= 80) return '#10b981'
    if (score >= 70) return '#3b82f6'
    if (score >= 60) return '#3b82f6'
    if (score >= 50) return '#f59e0b'
    if (score >= 40) return '#f97316'
    return '#ef4444'
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 dark:text-white mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: data.bandColor }}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Score: <span className="font-medium text-gray-900 dark:text-white">{data.score}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Band: <span className="font-medium" style={{ color: data.bandColor }}>{data.band}</span>
              </span>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#e5e7eb"
            className="dark:stroke-gray-600"
          />
          
          <XAxis 
            dataKey="date"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            axisLine={{ stroke: '#d1d5db' }}
            tickLine={{ stroke: '#d1d5db' }}
          />
          
          <YAxis 
            domain={[0, 100]}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            axisLine={{ stroke: '#d1d5db' }}
            tickLine={{ stroke: '#d1d5db' }}
          />

          {/* Band reference lines */}
          {showBands && Object.entries(BAND_THRESHOLDS).map(([band, threshold]) => (
            threshold > 0 && threshold < 100 && (
              <ReferenceLine
                key={band}
                y={threshold}
                stroke={BAND_COLORS[band as ScoreBand]}
                strokeDasharray="2 2"
                strokeOpacity={0.4}
                label={{ value: band, position: 'right', fontSize: 10 }}
              />
            )
          ))}

          <Tooltip content={<CustomTooltip />} />
          
          <Area
            type="monotone"
            dataKey="score"
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#scoreGradient)"
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#fff' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}